import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { MarketingGlampointsService } from 'src/app/services/marketing-glampoints.service';

@Component({
  selector: 'app-glampoints-action-form',
  templateUrl: './glampoints-action-form.component.html',
  styleUrl: './glampoints-action-form.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class GlampointsActionFormComponent implements OnInit {
  private service = inject(MarketingGlampointsService);
  private title = inject(Title);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  actionId?: number;
  isEditing = false;
  // Ação gerada já creditou glampoints — vira registro imutável (o backend também bloqueia).
  generated = false;

  form = new FormGroup({
    name: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(200)]
    }),
    description: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.maxLength(500)]
    }),
    points: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(1)
    ]),
    active: new FormControl<boolean>(true, { nonNullable: true })
  });

  async ngOnInit(): Promise<void> {
    const id = this.route.snapshot.paramMap.get('actionId');
    if (id) {
      this.actionId = Number(id);
      this.isEditing = true;
      this.title.setTitle('Marketing - Editar Ação de Glampoints');
      await this.loadAction();
    } else {
      this.title.setTitle('Marketing - Nova Ação de Glampoints');
    }
  }

  invalidControl(control: string): boolean {
    return (
      this.form.controls[control].invalid &&
      (this.form.controls[control].dirty || this.form.controls[control].touched)
    );
  }

  async loadAction(): Promise<void> {
    LoaderService.showLoader();
    try {
      const action = await lastValueFrom(this.service.getAction(this.actionId!));
      this.form.patchValue({
        name: action.name,
        description: action.description ?? '',
        points: action.points,
        active: action.status === 'ACTIVE'
      });
      this.generated = action.status === 'GENERATED';
      if (this.generated) this.form.disable();
    } catch (error) {
      AppDialogService.showErrorDialog(error, true);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async submit(): Promise<void> {
    if (this.generated) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const body = {
      name: this.form.controls.name.value.trim(),
      description: this.form.controls.description.value?.trim() || undefined,
      points: this.form.controls.points.value!,
      status: this.form.controls.active.value ? 'ACTIVE' : 'INACTIVE'
    };
    LoaderService.showLoader();
    try {
      if (this.isEditing && this.actionId) {
        await lastValueFrom(this.service.updateAction(this.actionId, body));
      } else {
        await lastValueFrom(this.service.createAction(body));
      }
      await this.router.navigate(['/marketing/glampoints-actions']);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  cancel(): void {
    this.router.navigate(['/marketing/glampoints-actions']);
  }
}
